import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useCallback, useMemo, useState, useRef } from 'react';
import { createUseStyles, Advice, Button, CheckboxGroup, Input, Link, Password, Text, useTheme } from '@Sage/hoi-poi-ui';
import { FadeTransition } from "../../../components/Transitions";
import useLoginLiterals from "../../../lib/useLoginLiterals";
import PasswordPolicies from "../../authentication/components/PasswordPolicies";
import { Header, Layout } from "../components";
import styles from "./styles.js";
const defaultSettings = {
  title: 'label_accept_invitation_title',
  privacyPolicyUrl: 'label_privacy_policy_url',
  termsAndConditionsUrl: 'label_terms_and_conditions_url',
  accountUrl: 'label_account_url',
  hasNewsletter: true
};
const useStyles = createUseStyles(styles, 'InvitationForm');
const licensesWithoutTC = ['sage50fr_starter', 'sage50fr_professional', 'sage100fr_starter', 'sage100fr_professional'];
const InvitationForm = _ref => {
  let {
    logo,
    email,
    onSend,
    onLogin,
    policies,
    settings = defaultSettings,
    license
  } = _ref;
  const isValidatingForm = useRef(false);
  const touchedRef = useRef({});
  const theme = useTheme();
  const [state, setState] = useState({
    error: null,
    loading: false
  });
  const [formState, setFormState] = useState({
    fullName: '',
    email: email,
    password: '',
    confirmPassword: '',
    checks: {
      privacyPolicy: false,
      termsAndConditions: false,
      newsletter: false
    }
  });
  const [errors, setErrors] = useState({});
  const classes = useStyles();
  const {
    loading,
    t,
    getLiteralWithParametersHtml
  } = useLoginLiterals();
  const isRequired = useCallback(value => !value ? t('helptext_required_field') : false, [t]);
  const validateField = useCallback((field, value) => {
    if (field === 'checks' && !isValidatingForm.current) return;
    if (isValidatingForm.current) touchedRef.current = _objectSpread(_objectSpread({}, touchedRef.current), {}, {
      [field]: true
    });
    let error = null;
    switch (field) {
      case 'fullName':
        error = isRequired(value);
        break;
      case 'password':
        error = isRequired(value) ? isRequired(value) : !policies.every(policy => policy.condition(value)) ? t('error_password_policies') : false;
        break;
      case 'confirmPassword':
        error = isRequired(value) ? isRequired(value) : formState.password !== value ? t('error_password_validation') : false;
        break;
      case 'checks':
        if (!value.privacyPolicy || !value.termsAndConditions && !licensesWithoutTC.includes(license)) {
          error = {
            privacyPolicy: !value.privacyPolicy ? t('helptext_required_field') : false,
            termsAndConditions: !value.termsAndConditions ? t('helptext_required_field') : false
          };
        } else {
          error = false;
        }
        break;
      default:
        break;
    }
    setErrors(_objectSpread(_objectSpread({}, errors), {}, {
      [field]: error
    }));
    return error;
  }, [errors, formState.password, isRequired, license, policies, t]);
  const validForm = useMemo(() => {
    return Object.values(errors).filter(Boolean).length === 0;
  }, [errors]);
  const validateForm = useCallback(() => {
    const errors = Object.entries(formState).reduce((obj, _ref2) => {
      let [field, value] = _ref2;
      return _objectSpread(_objectSpread({}, obj), {}, {
        [field]: validateField(field, value)
      });
    }, {});
    setErrors(errors);
    return Object.values(errors).filter(Boolean).length === 0;
  }, [validateField, formState]);
  const onBlur = useCallback(field => {
    touchedRef.current = _objectSpread(_objectSpread({}, touchedRef.current), {}, {
      [field]: true
    });
  }, []);
  const onChange = useCallback(field => value => {
    setFormState(_objectSpread(_objectSpread({}, formState), {}, {
      [field]: value
    }));
    validateField(field, value);
  }, [formState, validateField]);
  const handleLink = useCallback(function (e, url, target) {
    if (target === void 0) {
      target = '_blank';
    }
    e.stopPropagation();
    window.open(url, target);
  }, []);
  const handleLogin = useCallback(() => {
    onLogin(formState.email, formState.password).catch(e => {
      let error = 'error_generalerror';
      if (typeof e === 'string') error = e;
      setState({
        error: t(error),
        loading: false
      });
      isValidatingForm.current = false;
    });
  }, [formState, onLogin, t]);
  const handleSend = useCallback(e => {
    var _e$preventDefault;
    e === null || e === void 0 ? void 0 : (_e$preventDefault = e.preventDefault) === null || _e$preventDefault === void 0 ? void 0 : _e$preventDefault.call(e);
    isValidatingForm.current = true;
    if (validateForm()) {
      setState({
        error: null,
        loading: true
      });
    } else {
      return;
    }
    onSend({
      name: formState.fullName,
      password: formState.password,
      privatePolicy: formState.checks.privacyPolicy,
      termsAndConditions: licensesWithoutTC.includes(license) ? true : formState.checks.termsAndConditions,
      newsletter: formState.checks.newsletter
    }).then(() => {
      handleLogin(formState.email, formState.password);
    }).catch(e => {
      setState({
        error: t('error_generalerror'),
        loading: false
      });
      console.error(e);
    });
  }, [formState.checks.newsletter, formState.checks.privacyPolicy, formState.checks.termsAndConditions, formState.email, formState.fullName, formState.password, handleLogin, license, onSend, t, validateForm]);
  const getLabelWithLink = useCallback(_ref3 => {
    let {
      label,
      link,
      url,
      target
    } = _ref3;
    return getLiteralWithParametersHtml(t(label), [t(url)], url => /*#__PURE__*/React.createElement(Link, {
      className: classes.link,
      onClick: e => handleLink(e, url, target)
    }, t(link)));
  }, [classes.link, getLiteralWithParametersHtml, handleLink, t]);
  const getChecksOptions = useMemo(() => {
    const options = [{
      label: /*#__PURE__*/React.createElement(Text, {
        type: "body",
        color: theme.colors.textLight.primary
      }, getLabelWithLink({
        label: 'label_privacy_policy',
        link: 'label_privacy_policy_link',
        url: settings.privacyPolicyUrl
      })),
      value: 'privacyPolicy'
    }];
    if (!licensesWithoutTC.includes(license)) options.push({
      label: /*#__PURE__*/React.createElement(Text, {
        type: "body",
        color: theme.colors.textLight.primary
      }, getLabelWithLink({
        label: 'label_terms_and_conditions',
        link: 'label_terms_and_conditions_link',
        url: settings.termsAndConditionsUrl
      })),
      value: 'termsAndConditions'
    });
    if (settings.hasNewsletter) {
      options.push({
        label: /*#__PURE__*/React.createElement(Text, {
          type: "body",
          color: theme.colors.textLight.primary
        }, t('label_newsletter')),
        value: 'newsletter'
      });
    }
    return options;
  }, [theme.colors.textLight.primary, getLabelWithLink, settings.privacyPolicyUrl, settings.termsAndConditionsUrl, settings.hasNewsletter, license, t]);
  if (loading) return null;
  const touched = touchedRef.current;
  return /*#__PURE__*/React.createElement(Layout, {
    className: classes.root
  }, /*#__PURE__*/React.createElement("form", {
    className: classes.canvas,
    onSubmit: handleSend,
    autoComplete: "off"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.logo
  }, logo), /*#__PURE__*/React.createElement(Header, {
    title: t(settings.title),
    description: t('label_accept_invitation_description'),
    className: classes.header
  }), /*#__PURE__*/React.createElement(FadeTransition, {
    transitionKey: "error",
    show: state.error
  }, /*#__PURE__*/React.createElement(Advice, {
    className: classes.advice,
    type: "error",
    showIcon: true,
    defaultCollapsed: false,
    showCollapse: false
  }, state.error)), /*#__PURE__*/React.createElement(Input, {
    label: t('label_login_email'),
    value: formState.email,
    isReadOnly: true,
    isFullWidth: true
  }), /*#__PURE__*/React.createElement(Input, {
    label: t('label_full_name'),
    placeholder: t('placeholder_name'),
    isRequired: true,
    value: formState.fullName,
    error: touched.fullName && errors.fullName,
    onChange: onChange('fullName'),
    onBlur: () => onBlur('fullName'),
    isFullWidth: true,
    role: "presentation",
    autocomplete: "off"
  }), /*#__PURE__*/React.createElement(Password, {
    label: t('label_password'),
    placeholder: t('placeholder_password_confirmation'),
    isRequired: true,
    value: formState.password,
    error: touched.password && errors.password,
    onChange: onChange('password'),
    onBlur: () => onBlur('password'),
    isFullWidth: true
  }), /*#__PURE__*/React.createElement(PasswordPolicies, {
    policies: policies,
    t: t,
    password: formState.password,
    className: classes.policies
  }), /*#__PURE__*/React.createElement(Password, {
    label: t('label_password_confirmation'),
    placeholder: t('placeholder_password_confirmation'),
    isRequired: true,
    value: formState.confirmPassword,
    error: touched.confirmPassword && errors.confirmPassword,
    onChange: onChange('confirmPassword'),
    onBlur: () => onBlur('confirmPassword'),
    isFullWidth: true
  }), /*#__PURE__*/React.createElement(CheckboxGroup, {
    options: getChecksOptions,
    onChange: onChange('checks'),
    value: formState.checks,
    isFullWidth: true,
    className: classes.checkboxGroup,
    error: errors.checks,
    color: "actionMajor"
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.formActions
  }, /*#__PURE__*/React.createElement(Text, {
    type: "caption",
    className: classes.formActionsLink
  }, getLabelWithLink({
    label: 'label_account',
    link: 'label_account_link',
    url: settings.accountUrl,
    target: '_self'
  })), /*#__PURE__*/React.createElement(Button, {
    type: "primary",
    size: "big",
    isLoading: state.loading,
    isDisabled: !validForm && isValidatingForm.current,
    overrides: {
      root: {
        type: 'submit'
      }
    }
  }, t('label_join_now')))));
};
export default /*#__PURE__*/memo(InvitationForm);